import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import Sidebar from "../../DesignSystem/Sidebar";
import Breadcrumb from "../../DesignSystem/Breadcrumb/Breadcrumb";
import PrimaryButton from "../../DesignSystem/Buttons/PrimaryButton";
import FileUpload from "../../DesignSystem/Upload/FileUpload";
import UploadedFileList from "../../DesignSystem/Upload/UploadedFileList";
import "../../../css/Finance/FCIStatements.css";

const ACCEPTED_FILE_TYPES = ".xlsx,.xls,.csv";
const RECIPIENT_SHEET_NAME = "Commission Payments";
const REVISED_SHEET_NAME = "Revised";

const parsePayableValue = (value) => {
  if (typeof value === "number") return value;
  if (typeof value !== "string" || !value.trim()) return null;

  const trimmedValue = value.trim();
  const isNegativeInBrackets = /^\(.*\)$/.test(trimmedValue);
  const numericValue = Number(
    trimmedValue.replace(/[£$€,\s()]/g, ""),
  );

  if (!Number.isFinite(numericValue)) return null;
  return isNegativeInBrackets ? -numericValue : numericValue;
};

const normalizeHeader = (value) =>
  String(value).trim().toLowerCase().replace(/[^a-z0-9]/g, "");

const isDateHeader = (value) =>
  String(value)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .includes("date");

const isCellAddress = (key) => /^[A-Z]+[1-9][0-9]*$/.test(key);

const hasCellValue = (cell) =>
  cell &&
  (cell.f ||
    cell.v !== undefined ||
    (typeof cell.w === "string" && cell.w.trim()));

const getSheetUsedRange = (sheet) => {
  const cellAddresses = Object.keys(sheet).filter(
    (key) => isCellAddress(key) && hasCellValue(sheet[key]),
  );
  if (!cellAddresses.length) return null;

  return cellAddresses.reduce((range, address) => {
    const cell = XLSX.utils.decode_cell(address);
    return {
      s: {
        r: Math.min(range.s.r, cell.r),
        c: Math.min(range.s.c, cell.c),
      },
      e: {
        r: Math.max(range.e.r, cell.r),
        c: Math.max(range.e.c, cell.c),
      },
    };
  }, {
    s: { r: Number.MAX_SAFE_INTEGER, c: Number.MAX_SAFE_INTEGER },
    e: { r: 0, c: 0 },
  });
};

const normalizeSheetRef = (sheet) => {
  const usedRange = getSheetUsedRange(sheet);
  if (!usedRange) {
    delete sheet["!ref"];
    return null;
  }

  sheet["!ref"] = XLSX.utils.encode_range(usedRange);
  return usedRange;
};

const copySheetWithUsedRange = (sheet) => {
  const copy = {};
  Object.keys(sheet).forEach((key) => {
    if (!isCellAddress(key) || !hasCellValue(sheet[key])) return;
    copy[key] = { ...sheet[key] };
  });

  if (sheet["!cols"]) copy["!cols"] = sheet["!cols"];
  normalizeSheetRef(copy);
  return copy;
};

const removeNonPositivePayableRows = (sheet) => {
  const rows = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: "",
    raw: true,
  });
  const displayedRows = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: "",
    raw: false,
  });
  const headerRowIndex = rows.findIndex((row) =>
    row.some(
      (cell) => String(cell).trim().toLowerCase() === "payable",
    ),
  );

  if (headerRowIndex === -1) return sheet;

  const payableColumnIndex = rows[headerRowIndex].findIndex(
    (cell) => String(cell).trim().toLowerCase() === "payable",
  );
  const payawaysColumnIndex = rows[headerRowIndex].findIndex(
    (cell) =>
      String(cell).trim().toLowerCase() === "payaways made/received",
  );
  const recipientColumnIndex = rows[headerRowIndex].findIndex(
    (cell) => normalizeHeader(cell) === "recipient",
  );
  const sellingAdviserColumnIndex = rows[headerRowIndex].findIndex(
    (cell) => normalizeHeader(cell) === "sellingadviser",
  );
  const dateColumnIndexes = rows[headerRowIndex].reduce(
    (indexes, cell, columnIndex) => {
      if (isDateHeader(cell)) indexes.push(columnIndex);
      return indexes;
    },
    [],
  );
  rows.forEach((row, rowIndex) => {
    if (rowIndex <= headerRowIndex) return;
    dateColumnIndexes.forEach((columnIndex) => {
      const displayedValue =
        displayedRows[rowIndex]?.[columnIndex] ?? row[columnIndex];
      row[columnIndex] = formatUkDate(displayedValue);
    });
  });
  const filteredRows = rows.filter((row, rowIndex) => {
    if (rowIndex <= headerRowIndex) return true;
    const payableValue = parsePayableValue(row[payableColumnIndex]);
    const payawaysValue =
      payawaysColumnIndex === -1
        ? ""
        : String(row[payawaysColumnIndex]).trim().toLowerCase();
    const isPayawayReceived = [
      "payaway received",
      "payway received",
    ].includes(payawaysValue);

    return payableValue !== null && payableValue !== 0 && !isPayawayReceived;
  });
  const revisedRows = filteredRows.map((row, rowIndex) => {
    if (rowIndex <= headerRowIndex || recipientColumnIndex === -1) return row;
    if (String(row[recipientColumnIndex] ?? "").trim()) return row;

    const sellingAdviser =
      sellingAdviserColumnIndex === -1
        ? ""
        : String(row[sellingAdviserColumnIndex] ?? "").trim();

    row[recipientColumnIndex] = sellingAdviser;
    return row;
  });
  const revisedSheet = XLSX.utils.aoa_to_sheet(revisedRows);

  if (sheet["!cols"]) revisedSheet["!cols"] = sheet["!cols"];
  normalizeSheetRef(revisedSheet);
  revisedRows.forEach((row, rowIndex) => {
    if (rowIndex <= headerRowIndex) return;
    dateColumnIndexes.forEach((columnIndex) => {
      const cell = revisedSheet[
        XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex })
      ];
      if (!cell) return;
      if (row[columnIndex] instanceof Date) {
        cell.t = "d";
        cell.v = row[columnIndex];
        cell.z = "dd/mm/yyyy";
        delete cell.w;
      }
    });
  });
  return revisedSheet;
};

function formatUkDate(value) {
  if (value === null || value === undefined || value === "") return "";

  let day;
  let month;
  let year;

  if (typeof value === "number") {
    const parsedDate = XLSX.SSF.parse_date_code(value);
    if (!parsedDate) return String(value);
    ({ d: day, m: month, y: year } = parsedDate);
  } else if (value instanceof Date && !Number.isNaN(value.getTime())) {
    day = value.getDate();
    month = value.getMonth() + 1;
    year = value.getFullYear();
  } else {
    const dateMatch = String(value)
      .trim()
      .match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/);
    if (!dateMatch) return String(value).trim();
    day = Number(dateMatch[1]);
    month = Number(dateMatch[2]);
    year = Number(dateMatch[3]);
    if (year < 100) year += 2000;
  }

  if (month < 1 || month > 12) return String(value);

  const parsedDate = new Date(year, month - 1, day);
  if (
    parsedDate.getFullYear() !== year ||
    parsedDate.getMonth() !== month - 1 ||
    parsedDate.getDate() !== day
  ) {
    return String(value).trim();
  }

  return parsedDate;
}

const buildRecipientSheet = (revisedSheet) => {
  const rows = XLSX.utils.sheet_to_json(revisedSheet, {
    header: 1,
    defval: "",
    raw: true,
  });
  const headerRowIndex = rows.findIndex((row) =>
    row.some((cell) => normalizeHeader(cell) === "recipient"),
  );

  if (headerRowIndex === -1) {
    return XLSX.utils.aoa_to_sheet([["Recipient"]]);
  }

  const recipientColumnIndex = rows[headerRowIndex].findIndex(
    (cell) => normalizeHeader(cell) === "recipient",
  );
  const recipientTypeColumnIndex = rows[headerRowIndex].findIndex(
    (cell) => normalizeHeader(cell) === "recipienttype",
  );
  const paymentDateColumnIndex = rows[headerRowIndex].findIndex(
    (cell) => normalizeHeader(cell) === "paymentdate",
  );
  const sellingAdviserColumnIndex = rows[headerRowIndex].findIndex(
    (cell) => normalizeHeader(cell) === "sellingadviser",
  );
  const clientNameColumnIndex = rows[headerRowIndex].findIndex(
    (cell) => normalizeHeader(cell) === "clientname",
  );
  const planNumberColumnIndex = rows[headerRowIndex].findIndex(
    (cell) => normalizeHeader(cell) === "plannumber",
  );
  const providerColumnIndex = rows[headerRowIndex].findIndex(
    (cell) => normalizeHeader(cell) === "provider",
  );
  const planTypeColumnIndex = rows[headerRowIndex].findIndex(
    (cell) => normalizeHeader(cell) === "plantype",
  );
  const incomeTypeColumnIndex = rows[headerRowIndex].findIndex(
    (cell) => normalizeHeader(cell) === "incometype",
  );
  const payableColumnIndex = rows[headerRowIndex].findIndex(
    (cell) => normalizeHeader(cell) === "payable",
  );
  const grossReceivedColumnIndex = rows[headerRowIndex].findIndex(
    (cell) => normalizeHeader(cell) === "grossfciexclvatgst",
  );
  const introducersColumnIndex = rows[headerRowIndex].findIndex(
    (cell) => normalizeHeader(cell) === "introducers",
  );
  const dataRows = rows.slice(headerRowIndex + 1);
  const sortedDataRows = [...dataRows].sort((firstRow, secondRow) => {
    const firstRecipient = String(
      firstRow[recipientColumnIndex] ?? "",
    ).trim();
    const secondRecipient = String(
      secondRow[recipientColumnIndex] ?? "",
    ).trim();

    return firstRecipient.localeCompare(secondRecipient, undefined, {
      sensitivity: "base",
    });
  });
  const recipientRows = sortedDataRows.reduce(
    (outputRows, row) => {
      const recipient = String(row[recipientColumnIndex] ?? "").trim();
      if (!recipient) return outputRows;

      const recipientType =
        recipientTypeColumnIndex === -1
          ? ""
          : String(row[recipientTypeColumnIndex] ?? "").trim();
      const paymentCreated = formatUkDate(
        paymentDateColumnIndex === -1
          ? ""
          : row[paymentDateColumnIndex],
      );
      const policyAdviser =
        sellingAdviserColumnIndex === -1
          ? ""
          : String(row[sellingAdviserColumnIndex] ?? "").trim();
      const clientName =
        clientNameColumnIndex === -1
          ? ""
          : String(row[clientNameColumnIndex] ?? "").trim();
      const policyNumber =
        planNumberColumnIndex === -1
          ? ""
          : String(row[planNumberColumnIndex] ?? "").trim();
      const policyProvider =
        providerColumnIndex === -1
          ? ""
          : String(row[providerColumnIndex] ?? "").trim();
      const businessType =
        planTypeColumnIndex === -1
          ? ""
          : String(row[planTypeColumnIndex] ?? "").trim();
      const commissionType =
        incomeTypeColumnIndex === -1
          ? ""
          : String(row[incomeTypeColumnIndex] ?? "").trim();
      const payable =
        payableColumnIndex === -1 ? "" : row[payableColumnIndex];
      const grossReceived =
        grossReceivedColumnIndex === -1
          ? ""
          : row[grossReceivedColumnIndex];
      const introducerName =
        introducersColumnIndex === -1
          ? ""
          : String(row[introducersColumnIndex] ?? "").trim();
      outputRows.push([
        recipientType ? `${recipient} - ${recipientType}` : recipient,
        paymentCreated,
        "",
        policyAdviser,
        clientName,
        policyNumber,
        policyProvider,
        businessType,
        commissionType,
        payable,
        grossReceived,
        payable,
        introducerName,
      ]);
      return outputRows;
    },
    [[
      "Recipient",
      "Payment Created",
      "Payment Date",
      "Policy Adviser",
      "Client Name",
      "Policy/Account Number",
      "Policy Provider",
      "Business Type",
      "Commission Type",
      "Payment Amount",
      "Gross Received",
      "Total Payment",
      "Introducer Name",
    ]],
  );
  const recipientSheet = XLSX.utils.aoa_to_sheet(recipientRows);

  recipientSheet["!cols"] = [
    { wch: 45 },
    { wch: 20 },
    { wch: 20 },
    { wch: 24 },
    { wch: 40 },
    { wch: 26 },
    { wch: 30 },
    { wch: 34 },
    { wch: 26 },
    { wch: 20 },
    { wch: 20 },
    { wch: 22 },
    { wch: 35 },
  ];
  normalizeSheetRef(recipientSheet);
  for (let rowIndex = 1; rowIndex < recipientRows.length; rowIndex += 1) {
    const paymentCreatedCell = recipientSheet[
      XLSX.utils.encode_cell({ r: rowIndex, c: 1 })
    ];
    if (!paymentCreatedCell) continue;

    const paymentCreated = recipientRows[rowIndex][1];
    if (paymentCreated instanceof Date) {
      paymentCreatedCell.t = "d";
      paymentCreatedCell.v = paymentCreated;
      paymentCreatedCell.z = "dd/mm/yyyy";
      delete paymentCreatedCell.w;
    }
  }
  return recipientSheet;
};

const reviseWorkbook = (workbook) => {
  const sourceSheetName = workbook.SheetNames[0];
  const originalSheet = copySheetWithUsedRange(workbook.Sheets[sourceSheetName]);
  const revisedSheet = removeNonPositivePayableRows(
    originalSheet,
  );
  const recipientSheet = buildRecipientSheet(revisedSheet);
  const outputWorkbook = XLSX.utils.book_new();

  normalizeSheetRef(originalSheet);

  XLSX.utils.book_append_sheet(
    outputWorkbook,
    recipientSheet,
    RECIPIENT_SHEET_NAME,
  );
  XLSX.utils.book_append_sheet(
    outputWorkbook,
    revisedSheet,
    REVISED_SHEET_NAME,
  );
  return outputWorkbook;
};

const buildDownloadName = () => {
  const today = new Date();
  const date = [
    String(today.getDate()).padStart(2, "0"),
    String(today.getMonth() + 1).padStart(2, "0"),
    today.getFullYear(),
  ].join("-");

  return `Commission Payments Format - ${date}.xlsx`;
};

const FCIStatementsPage = () => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const handleFileChange = (selectedFile) => {
    if (!selectedFile) return;

    if (!/\.(xlsx|xls|csv)$/i.test(selectedFile.name)) {
      setFile(null);
      setError("Please select an Excel workbook or CSV file (.xlsx, .xls or .csv).");
      return;
    }

    setFile(selectedFile);
    setError("");
  };

  const handleDownload = async () => {
    if (!file) return;

    setIsProcessing(true);
    setError("");

    try {
      const workbookData = await file.arrayBuffer();
      const workbook = XLSX.read(workbookData, {
        type: "array",
      });
      const revisedWorkbook = reviseWorkbook(workbook);
      const output = XLSX.write(revisedWorkbook, {
        bookType: "xlsx",
        type: "array",
      });

      saveAs(
        new Blob([output], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        buildDownloadName(),
      );
    } catch (downloadError) {
      console.error("Unable to revise FCI report", downloadError);
      setError("The workbook could not be processed. Please try another file.");
    } finally {
      setIsProcessing(false);
      setFile(null);
    }
  };

  return (
    <div className="Main-Page">
      <Helmet>
        <title>FCI Reports</title>
        <meta name="description" content="Upload and revise an FCI report." />
      </Helmet>

      <Sidebar />

      <main className="Main-Container column flex gap-m align-left">
        <Breadcrumb
          items={[
            { label: "Finance", to: "/Department/Finance" },
            { label: "FCI Reports" },
          ]}
        />
        <div className="fit-column gap-xs">
          <h3 className="FP_H3">FCI Reports</h3>
          <div className="FP_Help_Text">
            Get commission payments format from FCI reports.
          </div>
        </div>

        <FileUpload
          description="Drag & drop an Excel workbook or CSV file here or"
          browseText="Browse"
          accept={ACCEPTED_FILE_TYPES}
          files={file ? [file] : []}
          fileName={file?.name}
          status={error ? "error" : "default"}
          statusMessage={error}
          onFilesChange={handleFileChange}
          onRemove={() => setFile(null)}
        />
        {file && (
          <UploadedFileList files={[file]} onRemove={() => setFile(null)} />
        )}

        <PrimaryButton
          className="fci-statements-download-button"
          type="button"
          label={isProcessing ? "PROCESSING..." : "DOWNLOAD REVISED"}
          disabled={!file || isProcessing}
          onClick={handleDownload}
        />
      </main>
    </div>
  );
};

export default FCIStatementsPage;
